import { AsyncEvent } from "@/shared/synchronization";

/**
 * A channel for sending values from one "process" to another.
 */
export class Channel<T> implements AsyncIterable<T> {
  private queue: T[] = [];
  private valueAvailableEvent = new AsyncEvent();
  private closed = false;

  push(value: T): void {
    if (this.closed) {
      return;
    }

    this.queue.push(value);
    this.valueAvailableEvent.set(); // Signal that a value is available
  }

  close(): void {
    this.closed = true;
    this.valueAvailableEvent.set(); // Wake up any waiting iterators
  }

  [Symbol.asyncIterator](): AsyncIterator<T, void, void> {
    return {
      next: async (): Promise<IteratorResult<T, void>> => {
        // If values are already available, return immediately
        if (this.queue.length > 0) {
          return { value: this.queue.shift()!, done: false };
        }

        // If closed and no more values, done
        if (this.closed) {
          return { value: undefined, done: true };
        }

        // Wait for values to become available
        this.valueAvailableEvent.clear(); // Reset for next wait
        await this.valueAvailableEvent.wait();

        // After waking up, check if have values or are closed
        if (this.queue.length > 0) {
          const value = this.queue.shift();
          if (value === undefined) {
            throw new Error("impossible");
          }
          return { value, done: false };
        }

        // If woke up but no values, must be closed
        return { value: undefined, done: true };
      },
    };
  }
}
